/**
 * Created: 2026-05-24
 * Initial commit: Add coupon poster dialog for QR distribution flow
 *
 * Dialog that renders a printable coupon claim poster: site logo + name,
 * QR code for claiming the coupon, and coupon name / discount amount.
 *
 * Moved: 2026-05-26 — relocated from UserCouponsPage/ to components/;
 * accepts a discriminated source union (coupon | template) so callers
 * pass the object directly and the dialog owns all URL param logic.
 */

import { useQuery } from '@apollo/client/react'
import { currency } from '@homemaster/jslib'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@mui/material'
import domtoimage from 'dom-to-image-more'
import { useEffect, useState } from 'react'
import { type CouponCategory, SiteDocument, type SportCategory } from '../gql/graphql'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../lib/domainLabels'

interface CouponInfo {
  name: string
  category: CouponCategory
  sport: string | null | undefined
  discountAmount: number | null | undefined
}

export type CouponPosterSource =
  | { kind: 'coupon'; id: string; siteId: string; template: CouponInfo }
  | { kind: 'template'; id: string; siteId: string; info: CouponInfo }

interface CouponPosterDialogProps {
  onClose: () => void
  source: CouponPosterSource
}

export default function CouponPosterDialog({
  onClose,
  source,
}: CouponPosterDialogProps) {
  const [qrUrl, setQrUrl] = useState<string>()
  const [qrLoading, setQrLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const { data: siteData, loading: siteLoading } = useQuery(SiteDocument, {
    variables: { siteId: source.siteId },
    fetchPolicy: 'cache-first',
  })
  const site = siteData?.site

  useEffect(() => {
    let objectUrl: string
    setQrLoading(true)

    const fetchQr = async () => {
      const authToken = localStorage.getItem('authToken')
      const headers: HeadersInit = authToken
        ? { Authorization: `Bearer ${authToken}` }
        : {}

      const params = new URLSearchParams({ action: 'claimcpn', action_id: source.id })
      if (source.kind === 'template') params.set('extra_info', 't')

      const response = await fetch(`/api/actionqr?${params}`, { headers })
      objectUrl = URL.createObjectURL(await response.blob())
      setQrUrl(objectUrl)
      setQrLoading(false)
    }

    fetchQr()

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [source])

  const loading = siteLoading || qrLoading

  const info = source.kind === 'coupon' ? source.template : source.info
  const categoryLabel = COUPON_CATEGORY_LABEL[info.category] ?? info.category
  const sportLabel = info.sport
    ? (SPORT_LABEL[info.sport as SportCategory] ?? info.sport)
    : null
  const subtitle = [categoryLabel, sportLabel].filter(Boolean).join(' · ')

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          /* Poster card — this element will be rasterized for download */
          <Box
            id="coupon-poster-card"
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 3,
            }}
          >
            {/* Site header: logo left, name right */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
                alignSelf: 'stretch',
              }}
            >
              {site?.avatar ? (
                <Box
                  component="img"
                  src={`/api/imgproxy?name=${encodeURIComponent(site.avatar)}`}
                  alt={site.name}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                    {site?.name?.[0] ?? '?'}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                {site?.name ?? ''}
              </Typography>
            </Box>

            <Divider flexItem />

            {/* QR code */}
            {qrUrl && (
              <Box
                component="img"
                src={qrUrl}
                alt="扫码领取优惠券"
                sx={{ width: 200, height: 200 }}
              />
            )}

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              微信扫码领取优惠券
            </Typography>

            <Divider flexItem />

            {/* Coupon info */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                }}
              >
                {info.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {subtitle}
              </Typography>
              {info.discountAmount != null && (
                <Typography
                  variant="h5"
                  color="primary"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                >
                  {currency(info.discountAmount)}
                </Typography>
              )}
            </Box>
            {source.kind === 'coupon' && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 14,
                  color: 'black',
                  lineHeight: 1,
                }}
              >
                {source.id.slice(-8)}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={loading || downloading}
          onClick={async () => {
            const el = document.getElementById('coupon-poster-card')
            if (!el) return
            setDownloading(true)
            try {
              const dataUrl: string = await domtoimage.toPng(el, { scale: 2 })
              const a = document.createElement('a')
              a.href = dataUrl
              a.download = `coupon-poster-${source.id.slice(-8)}.png`
              a.click()
            } finally {
              setDownloading(false)
            }
          }}
        >
          下载海报
        </Button>
      </DialogActions>
    </Dialog>
  )
}
